import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';  // Import ToastContainer and toast
import 'react-toastify/dist/ReactToastify.css';  // Import styles for react-toastify

// Get token from localStorage
const token = localStorage.getItem("token");

const OrderDetailsPage = () => {
  const { orderId } = useParams();  // Extract order ID from URL params
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

// Fetch order details function outside of useEffect

const fetchOrderDetails = async (orderId, setOrderDetails, setLoading) => {
    try {
      const response = await fetch(buildApiUrl(`/api/orderItem/orderforstatus/${orderId}`), {
        method: "GET",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (response.ok) {
        const data = await response.json();
        setOrderDetails(data.order);
      } else {
        console.error("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Call fetchOrderDetails inside useEffect
    fetchOrderDetails(orderId, setOrderDetails, setLoading);
  }, [orderId]);

  // Function to handle status update
  const updateDeliveryStatus = async (itemCode, newStatus) => {
    const updateUrl = `buildApiUrl("/api/orderItem/order-items/${orderId}/update-status/${itemCode}`;
    const updatedStatus = { deliveryStatus: newStatus };

    const response = await fetch(updateUrl, {
      method: "PUT",
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedStatus),
    });

    if (response.ok) {
      const updatedItem = await response.json();
      // Update the state locally after the successful response
      const updatedItems = orderDetails.items.map(item => 
        item.itemCode === itemCode ? { ...item, deliveryStatus: newStatus } : item
      );
      setOrderDetails(prevDetails => ({
        ...prevDetails,
        items: updatedItems,
      }));
      fetchOrderDetails(orderId, setOrderDetails, setLoading)
      
      // Show success toast
      toast.success(`Delivery status updated to ${newStatus}!`);
    } else {
      // Show error toast if the update fails
      toast.error("Failed to update delivery status!");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-r from-blue-300 via-teal-200 to-green-200">
        <h2 className="text-xl font-semibold text-gray-800">Loading Order Details...</h2>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-r from-blue-300 via-teal-200 to-green-200">
        <h2 className="text-xl font-semibold text-gray-800">Order not found!</h2>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
      <div className="p-4">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-400 to-teal-400 shadow-lg rounded-lg p-3 mb-3">
          <h1 className="text-3xl font-bold text-center mb-4 text-white">Order Details</h1>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-700">{orderDetails.orderId}</h2>
              <p className="text-sm text-gray-600">Status: 
                <span className={`px-2 py-1 rounded-full text-white ${orderDetails.orderDeliveredStatus === "Dispatched" ? 'bg-green-500' : 'bg-red-500'}`}>
                  {orderDetails.orderDeliveredStatus}
                </span>
              </p>
              <p className="text-xs text-gray-500">Created At: {new Date(orderDetails.createdAt).toLocaleDateString()}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-medium text-gray-700">Total Amount: ₹{orderDetails.totalItemAmount}</p>
            </div>
          </div>
        </div>
  
        {/* Items Section */}
        <h2 className="text-2xl font-semibold mb-4 text-gray-700">Items</h2>
        <div className="overflow-x-auto max-w-[100vw] rounded-lg">
          <table className="min-w-full table-auto rounded-lg shadow-md border-collapse border-spacing-0">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="py-2 px-3 text-left text-xs font-semibold">Item Name</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Category</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Item Code</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Qty</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Price</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Total</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Delivery Status</th>
                <th className="py-2 px-3 text-left text-xs font-semibold">Update Status</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {orderDetails.items.map((item) => (
                <tr key={item.itemCode} className="border-b hover:bg-gray-100">
                  <td className="py-2 px-3 text-sm text-gray-700">{item.itemName}</td>
                  <td className="py-2 px-3 text-sm text-gray-700">{item.categoryName}</td>
                  <td className="py-2 px-3 text-sm text-gray-700">{item.itemCode}</td>
                  <td className="py-2 px-3 text-sm text-gray-700">{item.qty}</td>
                  <td className="py-2 px-3 text-sm text-gray-700">₹{item.price}</td>
                  <td className="py-2 px-3 text-sm text-gray-700">₹{item.totalAmount}</td>
                  <td className="py-2 px-3 text-sm text-gray-700">
                    <span className={`px-3 py-1 rounded-full text-white ${item.deliveryStatus === "Dispatched" ? 'bg-green-500' : 'bg-red-500'}`}>
                      {item.deliveryStatus}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-sm text-gray-700">
                    <select
                      className="p-1 border border-gray-300 rounded-md text-sm"
                      value={item.deliveryStatus}
                      onChange={(e) => updateDeliveryStatus(item.itemCode, e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Dispatched">Dispatched</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  
        {/* Toast Container */}
        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={true} closeOnClick pauseOnHover draggable />
      </div>
    </div>
  );
  
  
};

export default OrderDetailsPage;
